/**
 * Geração de respostas FAQ para AI Overviews / AEO.
 * Tom: E-E-A-T (dossier) — factual, sem estatísticas inventadas; dados GSC quando existirem.
 */

import type { AeoUiLanguage } from './aeoUtils';

export type AeoGscSnippet = {
  impressions?: number;
  clicks?: number;
  position?: number;
};

/** Garante comprimento alvo ~60–80 palavras (AI Overviews / resposta direta). */
function fitWordRange(text: string, minWords: number, maxWords: number): string {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.length <= maxWords && words.length >= minWords) {
    return words.join(' ');
  }
  if (words.length > maxWords) {
    return words.slice(0, maxWords).join(' ').replace(/\s*[,:;]\s*$/, '') + '.';
  }
  const padPT =
    ' Valide sempre com um especialista e atualize quando os dados da Search Console mudarem, mantendo transparência perante o cliente.';
  const padEN =
    ' Always validate with a subject-matter expert and refresh when Search Console data changes, staying transparent with stakeholders.';
  const pad = text.includes('Always validate') ? padEN : padPT;
  const merged = (text + pad).trim();
  const w = merged.split(/\s+/).filter((x) => x.length > 0);
  if (w.length > maxWords) return w.slice(0, maxWords).join(' ') + '.';
  return merged;
}

function metricsSentence(lang: AeoUiLanguage, g: AeoGscSnippet): string {
  const imp = g.impressions ?? 0;
  const clk = g.clicks ?? 0;
  const pos = g.position;
  if (imp <= 0 && clk <= 0 && (pos == null || !isFinite(pos))) {
    return lang === 'PT'
      ? 'Confirme volume, posição e CTR desta consulta na Google Search Console antes de publicar o FAQ.'
      : 'Confirm query volume, position and CTR in Google Search Console before publishing the FAQ.';
  }
  const posBit =
    pos != null && isFinite(pos)
      ? lang === 'PT'
        ? ` Posição média ~${pos.toFixed(1)}.`
        : ` Average position ~${pos.toFixed(1)}.`
      : '';
  if (lang === 'PT') {
    return `Nesta propriedade, a Search Console regista ${imp} impressões e ${clk} cliques para esta consulta no período analisado.${posBit} Use estes números para priorizar e contextualizar a resposta, sem extrapolar para outros mercados.`;
  }
  return `For this property, Search Console shows ${imp} impressions and ${clk} clicks for this query in the analyzed period.${posBit} Use these figures to prioritize and contextualize the answer without generalizing to other markets.`;
}

/**
 * Resposta 60–80 palavras, otimizada para ser citável em motores de resposta.
 * Não inventa tendências; ancora em GSC quando há métricas.
 */
export function generateAeoOverviewAnswer(query: string, opts: { language: AeoUiLanguage; gsc?: AeoGscSnippet }): string {
  const q = query.trim();
  const lang = opts.language;
  const g = opts.gsc ?? {};

  const m = metricsSentence(lang, g);

  const corePT = `Para responder a «${q}» em formato útil para AI Overviews: seja direto sobre a intenção da pergunta, descreva passos ou critérios verificáveis no site e evite promessas absolutas. ${m} Reforce experiência (o que já foi testado na propriedade), expertise (critérios técnicos) e confiança (fontes, limitações dos dados). Quem integra dados estruturados FAQ deve alinhar o texto à página de destino e rever após mudanças de algoritmo ou de dados GSC.`;

  const coreEN = `To answer "${q}" in a format useful for AI Overviews: address search intent directly, outline verifiable on-site steps or criteria, and avoid absolute claims. ${m} Emphasize experience (what you have tested on the property), expertise (technical criteria), and trust (sources, data limitations). Whoever adds FAQ structured data should align copy with the landing page and review after algorithm or GSC shifts.`;

  const raw = lang === 'PT' ? corePT : coreEN;
  return fitWordRange(raw, 60, 80);
}

export function buildSingleFaqJsonLd(question: string, answer: string): {
  '@context': string;
  '@type': string;
  mainEntity: unknown[];
} {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      },
    ],
  };
}

export function formatAeoJsonLdPretty(obj: unknown): string {
  return JSON.stringify(obj, null, 2);
}

/** FAQPage com várias perguntas (artigos do blog / landing). */
export function buildFaqPageJsonLdFromPairs(pairs: { question: string; answer: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: pairs.map((p) => ({
      '@type': 'Question',
      name: p.question,
      acceptedAnswer: { '@type': 'Answer', text: p.answer },
    })),
  };
}

/** Junta Speakable ao BlogPosting para motores de resposta (ex.: AI Overviews). */
export function attachSpeakableToBlogPosting(
  blogPosting: Record<string, unknown>,
  cssSelector: string[] = ['#aeo-answer'],
): Record<string, unknown> {
  return {
    ...blogPosting,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector,
    },
  };
}
